import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Header from '../components/Header';
import { portalCandidateService } from '../api/portalCandidateService';

type Recommendation = 'red' | 'yellow' | 'green';

export type PublishedAssessmentReport = {
  assessment: {
    id: number;
    name: string;
    date: string;
    location?: string | null;
  } | null;
  candidateLabel: string;
  finalRecommendation: Recommendation | null;
  growthRecommendations: string;
  mbcbRecommendations: string;
  publishedAt: string | null;
  sections: Array<{ roleId: number; roleName: string; content: string }>;
};

type PublishedReportsResponse = {
  success: boolean;
  data: PublishedAssessmentReport[];
};

export const usePublishedAssessmentReports = () => useQuery({
  queryKey: ['portalCandidate', 'assessmentFinalReports'],
  queryFn: async () => (await portalCandidateService.getAssessmentFinalReports()) as PublishedReportsResponse,
  retry: false,
});

const recommendationStyle: Record<Recommendation, { label: string; className: string }> = {
  green: { label: 'Green', className: 'bg-green-100 text-green-800' },
  yellow: { label: 'Yellow', className: 'bg-yellow-100 text-yellow-800' },
  red: { label: 'Red', className: 'bg-red-100 text-red-800' },
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const ReportBody: React.FC<{ report: PublishedAssessmentReport }> = ({ report }) => {
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    const assessmentId = report.assessment?.id;
    if (!assessmentId || isDownloading) return;
    try {
      setIsDownloading(true);
      setDownloadError(null);
      const blob = await portalCandidateService.getAssessmentFinalReportPdf(assessmentId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'assessment-report.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('Could not download the PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const recommendation = report.finalRecommendation ? recommendationStyle[report.finalRecommendation] : null;

  return (
    <div className="space-y-6">
      <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {report.assessment?.name || 'Assessment'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {[formatDate(report.assessment?.date), report.assessment?.location].filter(Boolean).join(' · ')}
            </p>
            {recommendation && (
              <span
                className={[
                  'mt-3 inline-flex rounded-full px-3 py-1 text-sm font-medium',
                  recommendation.className,
                ].join(' ')}
              >
                Recommendation: {recommendation.label}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {isDownloading ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>
        {downloadError && <p className="mt-2 text-sm text-red-700">{downloadError}</p>}
      </div>

      {report.sections.map((section) => (
        <div key={section.roleId} className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">{section.roleName}</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{section.content}</p>
        </div>
      ))}

      {report.growthRecommendations.trim() && (
        <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Growth Recommendations</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{report.growthRecommendations}</p>
        </div>
      )}

      {report.mbcbRecommendations.trim() && (
        <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">MBCB Recommendations</h3>
          <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{report.mbcbRecommendations}</p>
        </div>
      )}
    </div>
  );
};

const AssessmentReport: React.FC = () => {
  const { data, isLoading, error } = usePublishedAssessmentReports();
  const reports = data?.data || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Assessment Report</h1>
          <p className="text-gray-600 mt-2">
            What the assessment team wrote for you, along with their recommendations for your growth.
          </p>
        </div>

        {isLoading && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">Loading your report…</div>
        )}

        {error && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">
            Your report is not available yet.
          </div>
        )}

        {!isLoading && !error && reports.length === 0 && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
            <p className="text-gray-700">
              Your report has not been shared with you yet. The assessment team will let you know when it is ready.
            </p>
            <Link to="/dashboard" className="mt-3 inline-block text-sm text-primary-600 hover:text-primary-700">
              Back to dashboard
            </Link>
          </div>
        )}

        {reports.map((report) => (
          <ReportBody key={report.assessment?.id ?? report.publishedAt} report={report} />
        ))}
      </div>
    </div>
  );
};

export default AssessmentReport;
